import { isSavedAnalysis } from '@modules/analysis/helpers';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import {
  useFetchQuantifyReport,
  useGetQuantifyStateFromAnalysisStore,
  useGetSelectedGroupByOption,
  useGetSelectedMeasuredAsCriteriaOption,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { shallow } from 'zustand/shallow';

export const useResetQuantifyAnalysis = () => {
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const { hasQueryChanged, dispatch } = useQuantifyStore(
    ({ visual, dispatch }) => ({
      hasQueryChanged: visual.hasQueryChanged,
      dispatch,
    }),
    shallow,
  );
  const fetchQuantifyReport = useFetchQuantifyReport({
    isInitialFetch: true,
  });
  const id = useAnalysisStore(({ id }) => id);
  const parsedMetadata = useGetQuantifyStateFromAnalysisStore();

  const { isLoading: isLoadingGroupByOption, selectedGroupByOption } =
    useGetSelectedGroupByOption(parsedMetadata?.query?.groupByField?.field);

  const formulaID = (() => {
    if (parsedMetadata?.query?.formulaQuery?.eqlFunction) {
      return parsedMetadata?.query?.formulaQuery?.eqlFunction;
    }

    return EQLFunctionName.Count;
  })();

  const {
    isLoading: isLoadingMeasuredAsCriteriaOption,
    selectedMeasuredAsCriteriaOption,
  } = useGetSelectedMeasuredAsCriteriaOption({
    formulaID,
    measuredAsCriteria: parsedMetadata?.query?.formulaQuery?.criteria?.field,
    groupBy: selectedGroupByOption,
  });

  useFullscreenLoader({
    isLoading: isLoadingGroupByOption || isLoadingMeasuredAsCriteriaOption,
    isInvisible: true,
  });

  useUpdateEffect(() => {
    if (shouldFetchReportData) {
      fetchQuantifyReport();
    }
  }, [shouldFetchReportData]);

  const resetQuantifyAnalysis = useCallback(() => {
    if (!isSavedAnalysis(id)) {
      dispatch({
        type: QuantifyActionType.RESET_QUERY,
      });
      return;
    }

    if (hasQueryChanged) {
      dispatch({
        type: QuantifyActionType.INIT_QUERY,
        payload: {
          ...parsedMetadata?.query,
          groupByField: selectedGroupByOption,
          formulaQuery: {
            ...parsedMetadata?.query?.formulaQuery,
            criteria: selectedMeasuredAsCriteriaOption,
          },
        },
      });
      setShouldFetchReportData(true);
    }
  }, [
    dispatch,
    hasQueryChanged,
    id,
    parsedMetadata?.query,
    selectedGroupByOption,
    selectedMeasuredAsCriteriaOption,
  ]);

  return resetQuantifyAnalysis;
};
